import { Network } from '@capacitor/network'
import type { ActionPerformed, PushNotificationSchema, Token } from '@capacitor/push-notifications'
import { PushNotifications } from '@capacitor/push-notifications'
import { getDevice } from 'framework7'
import { f7 } from 'framework7-vue'
import { DateTime } from 'luxon'

import { logoutWithoutPrompt } from '@/assets/composables/auth'
import { recordFailure, showFailure } from '@/assets/composables/diagnostics'
import { registerDeviceToken, unregisterDeviceToken } from '@/assets/composables/endpoints'
import { failureDetails, isExpectedFailure } from '@/assets/composables/errorPolicy'
import { showPreloader } from '@/assets/composables/preloader'
import { ErrorCodes } from '@/assets/composables/types'
import store from '@/js/store'

const device = getDevice()
let fcmDeviceToken: string | null = null

export const oauthScopes: string[] = [
  'https://www.googleapis.com/auth/calendar',
  'https://www.googleapis.com/auth/user.organization.read',
  'https://www.googleapis.com/auth/user.birthday.read',
]

export const clearStore = async (): Promise<void> => {
  await Promise.all([
    store.dispatch('setUser', null),
    store.dispatch('setDefaultSettings', {}),
    store.dispatch('setReservations', []),
    store.dispatch('setHistoricalReservations', []),
    store.dispatch('setCardMissingReservations', []),
    store.dispatch('setCardLoans', { rows: [], total: 0 }),
    store.dispatch('setVehicles', []),
    store.dispatch('setBanInfo', null),
  ])
}

export const notify = (text: string, callback?: () => void): void => {
  f7.dialog.alert(text, callback)
}

export const globalAppError = (error: unknown = new Error('Startup failed')) => {
  void showFailure(error, {
    area: 'startup',
    operation: 'load',
    title: 'Chyba pri spustení aplikácie',
    message:
      'Nastala chyba v spojení so serverom. Skúste sa prihlásiť znova. Ak problém pretrváva, pošlite diagnostiku technickej podpore.',
    onDismiss: () => {
      void logoutWithoutPrompt()
    },
  })
}

export const processError = async (error: unknown, customMessage?: string): Promise<void> => {
  const failure = failureDetails(error)
  const customErrorCode = failure.code
  let message: string = ''
  const technical = !isExpectedFailure(error)

  if (customErrorCode) {
    switch (customErrorCode) {
      case ErrorCodes.noAvailableSlots:
        message =
          'Bohužiaľ sa medzičasom všetky dostupné parkovacie kapacity obsadili. Vyberte si prosím iný čas alebo dátum.'
        break
      case ErrorCodes.sameTimeReservation:
        message =
          'Rezervácia s rovnakým dátumom a časovým úsekom už existuje v systéme. Žiadne zmeny neboli vykonané.'
        break
      case ErrorCodes.reservationsNotAllowed:
        message = 'Rezervácie momentálne nie sú povolené.'
        break
      case ErrorCodes.reservationsLimitReached:
        message =
          'Bohužiaľ ste dosiahli limit rezervácií na tento mesiac. Vašu rezerváciu nie je možné vytvoriť.'
        break
      case ErrorCodes.resInPast:
        message = 'Bohužiaľ, nie je možné vytvoriť ani zrušiť rezerváciu s časom v minulosti.'
        break
      case ErrorCodes.resInFuture:
        message = 'Túto akciu nie je možné vykonať pre rezerváciu v budúcnosti.'
        break
      case ErrorCodes.todayAlldayExits:
        message =
          'Pre dnešný deň ste už vytvorili celodennú rezerváciu. Keďže dnešný deň už prebieha, nie je možné ju nahradiť inou rezerváciou.'
        break
      case ErrorCodes.badPartOfDayToday:
        message =
          'Ak chcete rezerváciu doplniť o ostatnú časť dňa, vyberte si prosím celodennú rezerváciu.'
        break
      case ErrorCodes.vehicleMustExist:
        message =
          'Pred vytvorením rezervácie je potrebné v aplikácii <b>zaregistrovať vozidlo</b>. Môžete tak urobiť v časti "Správa vozidiel".'
        break
      case ErrorCodes.ecvAlreadyInUse:
        message = 'Toto vozidlo je už priradené k vášmu účtu.'
        break
      case ErrorCodes.dateInPast:
        message = 'Vybraný dátum je v minulosti. Vyberte dnešný alebo budúci dátum.'
        break
      case ErrorCodes.dateTooFar:
        message = 'Vybraný dátum je príliš vzdialený. Vyberte dátum v povolenom rozsahu.'
        break
      case ErrorCodes.missingCardReturned:
        message = 'Chýba informácia o vrátení parkovacej karty.'
        break
      case ErrorCodes.cardLoanNotFound:
        message = 'Táto parkovacia karta už nie je dostupná. Obnovte zoznam kariet.'
        break
      case ErrorCodes.invalidCardLoanId:
        message = 'Odkaz na parkovaciu kartu nie je platný. Obnovte zoznam kariet.'
        break
      case ErrorCodes.cardReturnVerificationRequired:
        message =
          'Vrátenie tejto karty vyžaduje dodatočné overenie. Kontaktujte správcu parkovania.'
        break
      case ErrorCodes.missingSpecification:
        message = 'Chýba špecifikácia typu rezervácií.'
        break
      case ErrorCodes.roleConflict:
        message = 'Váš účet má konfliktné roly a nemôže byť aktivovaný. Kontaktujte podporu.'
        break
      case ErrorCodes.permissionDenied:
        message = 'Nemáte oprávnenie na vykonanie tejto akcie.'
        break
      case ErrorCodes.userBanned: {
        const bannedUntil = store.getters.banInfo?.value?.bannedUntil as DateTime | undefined
        const until = bannedUntil?.isValid ? bannedUntil.toLocaleString(DateTime.DATE_FULL) : null
        message = until
          ? `Keďže ste nevrátili parkovaciu kartu, do ${until} si nemôžete rezervovať parkovanie. Termíny po tomto dátume si môžete rezervovať bez obmedzení.`
          : 'Keďže ste nevrátili parkovaciu kartu, momentálne si nemôžete rezervovať parkovanie.'
        break
      }
      case ErrorCodes.databaseError:
        message = 'Niekde nastala chyba. Skúste to prosím neskôr.'
        break
      default:
        message = 'Niekde nastala chyba. Skúste to prosím neskôr.'
        break
    }
  } else {
    message = 'Niekde nastala chyba. Skúste to prosím neskôr.'
  }

  if (failure.status === 429 || failure.code === 'RATE_LIMIT_EXCEEDED') {
    message = 'Príliš veľa požiadaviek. Skúste to prosím neskôr.'
  }

  if (technical) {
    await showFailure(error, {
      area: 'api',
      operation: 'request',
      title: 'Operáciu sa nepodarilo dokončiť',
      message: customMessage || message,
    })
  } else {
    notify(customMessage || message)
  }
}

export const initNetworkListeners = () => {
  const message = 'Stratili ste pripojenie k internetu. Skontrolujte svoje pripojenie.'
  let closeNetworkPreloader: ReturnType<typeof showPreloader> | undefined
  let statusChanged = false

  const updateStatus = (connected: boolean) => {
    if (connected) {
      closeNetworkPreloader?.()
      closeNetworkPreloader = undefined
    } else if (!closeNetworkPreloader) {
      closeNetworkPreloader = showPreloader(message)
    }
  }

  Network.getStatus().then((status) => {
    // A delayed initial snapshot must not override a newer network event.
    if (!statusChanged) updateStatus(status.connected)
  })

  Network.addListener('networkStatusChange', (status) => {
    statusChanged = true
    updateStatus(status.connected)
  })
}

export const syncDeviceToken = async (): Promise<void> => {
  if (!fcmDeviceToken || !device.cordova) return

  try {
    const platform = device.ios ? 'ios' : 'android'
    await registerDeviceToken(fcmDeviceToken, platform)
  } catch (error) {
    void recordFailure(error, { area: 'push', operation: 'sync' })
  }
}

export const unsyncDeviceToken = async (): Promise<void> => {
  if (!fcmDeviceToken || !device.cordova) return

  try {
    await unregisterDeviceToken(fcmDeviceToken)
  } catch {
    // Silent failure — token will become stale and get cleaned up eventually
  }
}

export const initPushNotifications = async (): Promise<void> => {
  if (!device.cordova) return

  let operation = 'listeners'
  try {
    // Registration can emit immediately; install and await its listeners first.
    await PushNotifications.addListener('registrationError', (error) => {
      // Native callbacks supply { error: string }, not an Error with a stack.
      // Keep the callback location and let the common sanitizer inspect the cause.
      void recordFailure(new Error('Push registration failed', { cause: error }), {
        area: 'push',
        operation: 'registration',
      })
    })

    await PushNotifications.addListener('registration', (token: Token) => {
      fcmDeviceToken = token.value

      if (store.getters.isUserLoggedIn.value) {
        void syncDeviceToken()
      }
    })

    await PushNotifications.addListener(
      'pushNotificationReceived',
      (notification: PushNotificationSchema) => {
        if (notification.title || notification.body) {
          f7.dialog.alert(notification.body || '', notification.title || 'Notifikácia')
        }
      },
    )

    await PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (_action: ActionPerformed) => {
        // User tapped notification — app opens normally
        // Navigation based on action.notification.data can be added later
      },
    )

    operation = 'permissions'
    const result = await PushNotifications.requestPermissions()
    // A user declining notifications is an intended outcome, not a failure.
    if (result.receive !== 'granted') return

    operation = 'registration'
    await PushNotifications.register()
  } catch (error) {
    await recordFailure(error, { area: 'push', operation })
  }
}

export const loseFocus = (el?: HTMLElement) => {
  if (el && typeof el.blur === 'function') {
    el.blur()
  }
}
